import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { adminService } from '../../../services/adminService';

const AdminUserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('all');
  const [selectedTier, setSelectedTier] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  // Load users
  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const { data, error } = await adminService?.getAllUsers();
      if (error) throw error;
      setUsers(data || []);
    } catch (err) {
      setError(err?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  // Filter users
  const filteredUsers = users?.filter(user => {
    const matchesSearch = user?.full_name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         user?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesRole = selectedRole === 'all' || user?.role === selectedRole;
    const matchesTier = selectedTier === 'all' || user?.membership_tier === selectedTier;
    return matchesSearch && matchesRole && matchesTier;
  }) || [];

  // Handle user role update
  const handleRoleUpdate = async (userId, newRole) => {
    try {
      setActionLoading(userId);
      const { data, error } = await adminService?.updateUserRole(userId, newRole);
      if (error) throw error;
      
      setUsers(prev => prev?.map(user => 
        user?.id === userId ? { ...user, role: newRole } : user
      ));
    } catch (err) {
      setError(err?.message || 'Failed to update role');
    } finally {
      setActionLoading(null);
    }
  };

  // Handle user status toggle
  const handleStatusToggle = async (userId, currentStatus) => {
    try {
      setActionLoading(userId);
      const newStatus = !currentStatus;
      const { data, error } = await adminService?.toggleUserStatus(userId, newStatus);
      if (error) throw error;
      
      setUsers(prev => prev?.map(user => 
        user?.id === userId ? { ...user, is_active: newStatus } : user
      ));
    } catch (err) {
      setError(err?.message || 'Failed to update status');
    } finally {
      setActionLoading(null);
    }
  };

  // Handle membership tier update
  const handleTierUpdate = async (userId, newTier) => {
    try {
      setActionLoading(userId);
      const { data, error } = await adminService?.updateMembershipTier(userId, newTier);
      if (error) throw error;
      
      setUsers(prev => prev?.map(user => 
        user?.id === userId ? { ...user, membership_tier: newTier } : user
      ));
    } catch (err) {
      setError(err?.message || 'Failed to update membership tier');
    } finally {
      setActionLoading(null);
    }
  };

  // Handle user deletion
  const handleDeleteUser = async (userId, userName) => {
    if (!confirm(`Are you sure you want to delete ${userName}? This action cannot be undone.`)) {
      return;
    }

    try {
      setActionLoading(userId);
      const { error } = await adminService?.deleteUser(userId);
      if (error) throw error;
      
      setUsers(prev => prev?.filter(user => user?.id !== userId));
    } catch (err) {
      setError(err?.message || 'Failed to delete user');
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="flex items-center space-x-3 text-muted-foreground">
          <div className="w-5 h-5 border-2 border-primary/20 border-t-primary rounded-full animate-spin"></div>
          <span>Loading users...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-foreground">User Management</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Manage user accounts, roles, and permissions
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
        >
          <Icon name="UserPlus" size={16} />
          <span>Add User</span>
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
          <div className="flex items-center space-x-2">
            <Icon name="AlertTriangle" size={16} className="text-destructive" />
            <p className="text-destructive text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="relative">
          <Icon name="Search" size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>

        <select
          value={selectedRole}
          onChange={(e) => setSelectedRole(e?.target?.value)}
          className="px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
        >
          <option value="all">All Roles</option>
          <option value="admin">Admin</option>
          <option value="premium_member">Premium Member</option>
          <option value="member">Member</option>
        </select>

        <select
          value={selectedTier}
          onChange={(e) => setSelectedTier(e?.target?.value)}
          className="px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
        >
          <option value="all">All Tiers</option>
          <option value="free">Free</option>
          <option value="premium">Premium</option>
          <option value="gold">Gold</option>
        </select>

        <div className="flex items-center space-x-2 text-sm text-muted-foreground">
          <Icon name="Users" size={16} />
          <span>{filteredUsers?.length} users found</span>
        </div>
      </div>

      {/* Users Table */}
      <div className="border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">User</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Role</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Tier</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Joined</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredUsers?.map((user) => (
                <tr key={user?.id} className="hover:bg-muted/30">
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center">
                        {user?.avatar_url ? (
                          <img src={user?.avatar_url} alt="" className="w-8 h-8 rounded-full" />
                        ) : (
                          <Icon name="User" size={16} className="text-muted-foreground" />
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">{user?.full_name}</p>
                        <p className="text-sm text-muted-foreground">{user?.email}</p>
                      </div>
                    </div>
                  </td>
                  
                  <td className="px-6 py-4">
                    <select
                      value={user?.role}
                      onChange={(e) => handleRoleUpdate(user?.id, e?.target?.value)}
                      disabled={actionLoading === user?.id}
                      className="text-sm px-2 py-1 bg-background border border-border rounded text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                    >
                      <option value="member">Member</option>
                      <option value="premium_member">Premium Member</option>
                      <option value="admin">Admin</option>
                    </select>
                  </td>
                  
                  <td className="px-6 py-4">
                    <select
                      value={user?.membership_tier}
                      onChange={(e) => handleTierUpdate(user?.id, e?.target?.value)}
                      disabled={actionLoading === user?.id}
                      className="text-sm px-2 py-1 bg-background border border-border rounded text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                    >
                      <option value="free">Free</option>
                      <option value="premium">Premium</option>
                      <option value="gold">Gold</option>
                    </select>
                  </td>
                  
                  <td className="px-6 py-4">
                    <button
                      onClick={() => handleStatusToggle(user?.id, user?.is_active)}
                      disabled={actionLoading === user?.id}
                      className={`inline-flex items-center px-2.5 py-1.5 rounded-full text-xs font-medium transition-colors ${
                        user?.is_active
                          ? 'bg-green-100 text-green-800 dark:bg-green-800/20 dark:text-green-300' :'bg-red-100 text-red-800 dark:bg-red-800/20 dark:text-red-300'
                      }`}
                    >
                      <div className={`w-1.5 h-1.5 rounded-full mr-2 ${
                        user?.is_active ? 'bg-green-500' : 'bg-red-500'
                      }`}></div>
                      {user?.is_active ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  
                  <td className="px-6 py-4 text-sm text-muted-foreground">
                    {new Date(user?.created_at)?.toLocaleDateString()}
                  </td>
                  
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => setEditingUser(user)}
                        disabled={actionLoading === user?.id}
                        className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded transition-colors"
                        title="Edit user"
                      >
                        <Icon name="Edit2" size={14} />
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user?.id, user?.full_name)}
                        disabled={actionLoading === user?.id || user?.role === 'admin'}
                        className="p-2 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title={user?.role === 'admin' ? 'Cannot delete admin user' : 'Delete user'}
                      >
                        {actionLoading === user?.id ? (
                          <div className="w-3.5 h-3.5 border border-muted-foreground/20 border-t-muted-foreground rounded-full animate-spin"></div>
                        ) : (
                          <Icon name="Trash2" size={14} />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {filteredUsers?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="Users" size={48} className="mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No users found</h3>
          <p className="text-muted-foreground">
            {searchTerm || selectedRole !== 'all' || selectedTier !== 'all' ?'Try adjusting your filters' :'No users have been created yet'
            }
          </p>
        </div>
      )}
    </div>
  );
};

export default AdminUserManagement;